import React, {useEffect, useState} from 'react'
import {matchPath, useLocation, useNavigate} from 'react-router-dom'
import {useAuth0, withAuthenticationRequired} from '@auth0/auth0-react'
import Router from './Routes'
import SideNavigation from '@yaak/components/src/SideNavigation'
import bundledRelease from '../public/release.json'
import Profile from '@yaak/components/src/Profile'
import en from '@yaak/ds-admin/src/lang/en.json'
import de from '@yaak/ds-admin/src/lang/de.json'
import {IntlProvider} from 'react-intl'
import {getUserLanguage} from './helpers/users'
import Icon from '@yaak/components/src/Icon'
import {IconSizes, IconTypes} from '@yaak/components/src/Icon/Icon'
import {PairingResponse,} from '@yaak/components/services/api/api'
import Toast, {toastType} from '@yaak/components/src/Toast/Toast'
import {ToastContext} from '@yaak/components/context/toastContext'
import {displayWaitingPairingRequests} from './helpers/pairingRequests'
import style from './style.less'
import classNames from 'classnames'
import {isSimAdmin} from './utils/simAdmin'
import FlagIcon from '@mui/icons-material/Flag';
import {getAuth0Tenant} from "@yaak/ds-admin/src/helpers/helpers";

const sideNavigationLabelsSimAdmin = [
  [
    {
      icon: (
        <Icon
          name={'Partner'}
          size={IconSizes.large}
          type={IconTypes.regular}
        />
      ),
      text: 'Partners',
    },
    {
      icon: (
        <Icon
          name={'SteeringWheel'}
          size={IconSizes.large}
          type={IconTypes.regular}
        />
      ),
      text: 'Drives',
    },
    {
      icon: (
        <Icon name={'Star'} size={IconSizes.large} type={IconTypes.regular} />
      ),
      text: 'DS Admins',
    },
    {
      icon: (
        <Icon
          name={'Instructor'}
          size={IconSizes.large}
          type={IconTypes.regular}
        />
      ),
      text: 'Instructors',
    },
    {
      icon: (
        <Icon
          name={'Student'}
          size={IconSizes.large}
          type={IconTypes.regular}
        />
      ),
      text: 'Students',
    },
    {
      icon: (
        <Icon name={'VR'} size={IconSizes.large} type={IconTypes.regular} />
      ),
      text: 'Simulator',
      subItems: [
        { text: 'Rigs' },
        { text: 'Partner Modules' },
        { text: 'Sim Modules' },
        { text: 'Custom Modules' },
      ],
    },
  ],
]

const sideNavigationLabels = (waitingPairingRequests?: PairingResponse) => [
  [
    {
      icon: (
        <Icon
          name={'Partner'}
          size={IconSizes.large}
          type={IconTypes.regular}
        />
      ),
      text: 'Partners',
    },
    {
      icon: (
        <Icon
          name={'SteeringWheel'}
          size={IconSizes.large}
          type={IconTypes.regular}
        />
      ),
      text: 'Drives',
      subItems: [
        {
          text: 'On-road',
        },
        {
          text: 'Simulation',
        },
      ],
    },
    {
      icon: (
        <Icon name={'Star'} size={IconSizes.large} type={IconTypes.regular} />
      ),
      text: 'DS Admins',
    },
    {
      icon: (
        <Icon
          name={'Instructor'}
          size={IconSizes.large}
          type={IconTypes.regular}
        />
      ),
      text: 'Instructors',
    },
    {
      icon: (
        <Icon
          name={'Student'}
          size={IconSizes.large}
          type={IconTypes.regular}
        />
      ),
      text: 'Students',
    },
    {
      icon: (
        <Icon name={'Car'} size={IconSizes.large} type={IconTypes.regular} />
      ),
      text: 'Vehicles',
    },
    {
      icon: (
        <Icon name={'VR'} size={IconSizes.large} type={IconTypes.regular} />
      ),
      text: 'Simulator',
      subItems: [
        { text: 'Rigs' },
        { text: 'Partner Modules' },
        { text: 'Sim Modules' },
        { text: 'Custom Modules' },
      ],
    },
    {
      icon: (
        <Icon name={'Device'} size={IconSizes.large} type={IconTypes.regular} />
      ),
      text: 'Kits',
      badge: displayWaitingPairingRequests(waitingPairingRequests),
    },
    {
      icon: (
        <FlagIcon />
      ),
      text: 'Feature Flags',
    }
  ],
]

const sideNavigationTooltips = {
  collapse: 'Collapse menu',
  expand: 'Expand menu',
  knowledgeBase: 'Knowledge base',
}

export const LIGHT_THEME = 'light'
export const DARK_THEME = 'dark'

interface ThemeTypeContext {
  theme: string
  setTheme: React.Dispatch<React.SetStateAction<string>>
}
export const ThemeContext = React.createContext<ThemeTypeContext>({
  theme: LIGHT_THEME,
  setTheme: () => {},
})

const App = () => {
  const [page, setPage] = useState(-1)
  const [token, setToken] = useState<string>('')
  const [toast, setShowToast] = useState<toastType | null>(null)
  const [theme, setTheme] = useState(LIGHT_THEME)
  const [tenant, setTenant] = useState<string | null>(null)

  const { getAccessTokenSilently } = useAuth0()
  const navigate = useNavigate()
  const location = useLocation()

  const { user } = useAuth0()
  const language = getUserLanguage(user)
  const simAdmin = isSimAdmin(user)

  useEffect(() => {
    const getToken = async () => {
      const token = await getAccessTokenSilently()
      setToken(token)
    }
    getToken()
  }, [])

  useEffect(() => {
    setTenant(getAuth0Tenant(user))
  }, [user])

  useEffect(() => {
    if (location) {
      simAdmin
        ? setCurrentPageIndexSimAdmin(location.pathname)
        : setCurrentPageIndex(location.pathname)
    }
  }, [location])

  const setCurrentPageIndexSimAdmin = (pathname: string) => {
    if (matchPath({ path: '/partners', end: false }, pathname)) {
      setPage(0)
    } else if (
      matchPath({ path: '/drives/simulation', end: false }, pathname)
    ) {
      setPage(1)
    } else if (matchPath({ path: '/dsadmins', end: false }, pathname)) {
      setPage(2)
    } else if (matchPath({ path: '/instructors', end: false }, pathname)) {
      setPage(3)
    } else if (matchPath({ path: '/students', end: false }, pathname)) {
      setPage(4)
    } else if (matchPath({ path: '/simulatorrigs', end: false }, pathname)) {
      setPage(5.1)
    } else if (matchPath({ path: '/simulator/custom-modules', end: false }, pathname)) {
      setPage(5.4)
    } else if (matchPath({ path: '/simulator/modules', end: false }, pathname)) {
      setPage(5.3)
    } else if (matchPath({ path: '/simulator/module-config', end: false }, pathname)) {
      setPage(5.2)
    } else {
      setPage(-1)
    }
  }

  const navigateToPageSimAdmin = (page: number) => {
    switch (page) {
      case 0:
        return navigate('/partners')
      case 1:
        return navigate('/drives/simulation/active')
      case 2:
        return navigate('/dsadmins')
      case 3:
        return navigate('/instructors')
      case 4:
        return navigate('/students')
      case 5:
      case 5.1:
        return navigate('/simulatorrigs')
      case 5.2:
        return navigate('/simulator/module-config')
      case 5.3:
        return navigate('/simulator/modules')
      case 5.4:
        return navigate('/simulator/custom-modules')
      default:
        return navigate('/')
    }
  }

  const setCurrentPageIndex = (pathname: string) => {
    if (matchPath({ path: '/partners', end: false }, pathname)) {
      setPage(0)
    } else if (matchPath({ path: '/drives/onroad', end: false }, pathname)) {
      setPage(1.1)
    } else if (
      matchPath({ path: '/drives/simulation', end: false }, pathname)
    ) {
      setPage(1.2)
    } else if (matchPath({ path: '/dsadmins', end: false }, pathname)) {
      setPage(2)
    } else if (matchPath({ path: '/instructors', end: false }, pathname)) {
      setPage(3)
    } else if (matchPath({ path: '/students', end: false }, pathname)) {
      setPage(4)
    } else if (matchPath({ path: '/vehicles', end: false }, pathname)) {
      setPage(5)
    } else if (matchPath({ path: '/simulatorrigs', end: false }, pathname)) {
      setPage(6.1)
    } else if (matchPath({ path: '/simulator/custom-modules', end: false }, pathname)) {
      setPage(6.4)
    } else if (matchPath({ path: '/simulator/modules', end: false }, pathname)) {
      setPage(6.3)
    } else if (matchPath({ path: '/simulator/module-config', end: false }, pathname)) {
      setPage(6.2)
    } else if (matchPath({ path: '/kits', end: false }, pathname)) {
      setPage(7)
    } else if (matchPath({ path: '/featureflags', end: false}, pathname)) {
      setPage(8)
    } else {
      setPage(-1)
    }
  }

  const navigateToPage = (page: number) => {
    switch (page) {
      case 0:
        return navigate('/partners')
      case 1:
        return navigate('/drives/onroad')
      case 1.1:
        return navigate('/drives/onroad')
      case 1.2:
        return navigate('/drives/simulation/active')
      case 2:
        return navigate('/dsadmins')
      case 3:
        return navigate('/instructors')
      case 4:
        return navigate('/students')
      case 5:
        return navigate('/vehicles')
      case 6:
      case 6.1:
        return navigate('/simulatorrigs')
      case 6.2:
        return navigate('/simulator/module-config')
      case 6.3:
        return navigate('/simulator/modules')
      case 6.4:
        return navigate('/simulator/custom-modules')
      case 7:
        return navigate('/kits/paired')
      case 8:
        return navigate('/featureflags')
      default:
        return navigate('/')
    }
  }

  const navigationLabels = simAdmin
    ? sideNavigationLabelsSimAdmin
    : sideNavigationLabels()

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
        <IntlProvider locale={language} messages={language === 'en' ? en : de}>
          <ToastContext.Provider value={{ toast, setShowToast }}>
              <div className={classNames(style[theme], style.app)}>
                <Profile
                  bundledRelease={bundledRelease}
                  onSettingsClicked={() => {
                    setPage(-1)
                  }}
                />
                <SideNavigation
                  admin={true}
                  labels={navigationLabels}
                  tooltips={sideNavigationTooltips}
                  currentPage={page}
                  onPageChange={(page) => {
                    setPage(page)
                    simAdmin ? navigateToPageSimAdmin(page) : navigateToPage(page)
                  }}
                />
                <div className={style.main}>
                  <Router token={token} tenant={tenant} />
                  {toast && <Toast toast={toast} setShowToast={setShowToast} />}
                </div>
              </div>
          </ToastContext.Provider>
        </IntlProvider>
    </ThemeContext.Provider>
  )
}

export default withAuthenticationRequired(App, {})
